import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { ACTIVITIES, SEED_TICKETS, TICKET_PACKAGE, ADMIN_USER } from "../data/mockData";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const LS_TICKETS = "bltr_tickets_v1";
const LS_ADMIN = "bltr_admin_v1";

const genCode = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let s = "";
  for (let i = 0; i < 6; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return `BLTR26-${s}`;
};
const genOrder = () => `ORD-${Math.floor(100000 + Math.random() * 899999)}`;
const genRef = () => `BK-${Math.floor(1000 + Math.random() * 8999)}`;

const toMin = (t) => {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};

export function AppProvider({ children }) {
  const [tickets, setTickets] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_TICKETS);
      return saved ? JSON.parse(saved) : SEED_TICKETS;
    } catch {
      return SEED_TICKETS;
    }
  });
  const [admin, setAdmin] = useState(() => {
    try {
      return localStorage.getItem(LS_ADMIN) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    localStorage.setItem(LS_TICKETS, JSON.stringify(tickets));
  }, [tickets]);

  const activities = ACTIVITIES;
  const getActivity = useCallback((idOrSlug) => activities.find((a) => a.id === idOrSlug || a.slug === idOrSlug), [activities]);
  const getSlot = useCallback((activityId, slotId) => {
    const a = getActivity(activityId);
    return a?.slots.find((s) => s.id === slotId);
  }, [getActivity]);

  // Count confirmed bookings for a given slot
  const slotBooked = useCallback((activityId, slotId) => {
    let n = 0;
    tickets.forEach((t) => t.bookings.forEach((b) => {
      if (b.activityId === activityId && b.slotId === slotId && b.status === "Confirmed") n++;
    }));
    return n;
  }, [tickets]);

  const slotRemaining = useCallback((activityId, slotId) => {
    const slot = getSlot(activityId, slotId);
    if (!slot) return 0;
    return Math.max(0, slot.capacity - slotBooked(activityId, slotId));
  }, [getSlot, slotBooked]);

  // Activity availability status
  const activityStatus = useCallback((activity) => {
    if (!activity.bookingRequired) return "no-booking";
    const totalCap = activity.slots.reduce((s, sl) => s + sl.capacity, 0);
    const totalBooked = activity.slots.reduce((s, sl) => s + slotBooked(activity.id, sl.id), 0);
    const remaining = totalCap - totalBooked;
    if (remaining <= 0) return "fully-booked";
    if (remaining <= totalCap * 0.25) return "almost-full";
    return "available";
  }, [slotBooked]);

  const getTicket = useCallback((code) => tickets.find((t) => t.ticketCode.toUpperCase() === (code || "").trim().toUpperCase()), [tickets]);

  const validateTicket = useCallback((code, contact) => {
    const t = getTicket(code);
    if (!t) return { ok: false, error: "Tiket tidak ditemukan. Cek kembali kode tiket kamu ya." };
    if (t.paymentStatus !== "Paid") return { ok: false, error: "Pembayaran tiket ini belum lunas. Selesaikan pembayaran dulu ya." };
    if (t.ticketStatus === "Cancelled") return { ok: false, error: "Tiket ini sudah dibatalkan." };
    if (contact) {
      const c = contact.trim().toLowerCase();
      const match = t.email.toLowerCase() === c || t.mobile.replace(/\s|-/g, "").includes(c.replace(/\s|-/g, ""));
      if (!match) return { ok: false, error: "Email/No. WhatsApp tidak cocok dengan tiket ini." };
    }
    return { ok: true, ticket: t };
  }, [getTicket]);

  const purchaseTicket = useCallback((buyer, participants) => {
    // participants: array of {parentName, childName, childAge, childGender, emergencyContact, emergencyPhone}
    const created = participants.map((p) => ({
      ticketCode: genCode(),
      orderNumber: buyer._order || genOrder(),
      buyerName: buyer.fullName,
      email: buyer.email,
      mobile: buyer.mobile,
      parentName: p.parentName || buyer.fullName,
      childName: p.childName,
      childAge: Number(p.childAge) || 0,
      childGender: p.childGender || "",
      emergencyContact: p.emergencyContact || "",
      emergencyPhone: p.emergencyPhone || "",
      paymentStatus: "Paid",
      ticketStatus: "Active",
      purchaseDate: new Date().toISOString().slice(0, 10),
      bookings: [],
    }));
    setTickets((prev) => [...created, ...prev]);
    return created;
  }, []);

  const bookActivity = useCallback((ticketCode, activityId, slotId) => {
    const t = getTicket(ticketCode);
    if (!t) return { ok: false, error: "Tiket tidak ditemukan." };
    if (t.paymentStatus !== "Paid") return { ok: false, error: "Tiket belum lunas." };
    const activity = getActivity(activityId);
    if (!activity) return { ok: false, error: "Aktivitas tidak ditemukan." };

    // duplicate activity check
    if (t.bookings.some((b) => b.activityId === activityId && b.status === "Confirmed")) {
      return { ok: false, error: "Kamu sudah booking aktivitas ini. Satu tiket hanya bisa booking sekali per aktivitas." };
    }
    const slot = getSlot(activityId, slotId);
    if (!slot) return { ok: false, error: "Slot tidak ditemukan." };

    // overlap check
    const overlap = t.bookings.some((b) => {
      if (b.status !== "Confirmed") return false;
      const s = getSlot(b.activityId, b.slotId);
      if (!s) return false;
      return toMin(slot.start) < toMin(s.end) && toMin(s.start) < toMin(slot.end);
    });
    if (overlap) return { ok: false, error: "Kamu sudah punya aktivitas lain di jam ini. Pilih slot waktu yang berbeda ya." };

    // capacity check
    if (slotRemaining(activityId, slotId) <= 0) return { ok: false, error: "Yah, slot ini sudah penuh. Masih banyak petualangan lain menunggu!" };

    const ref = genRef();
    setTickets((prev) => prev.map((x) => x.ticketCode === t.ticketCode
      ? { ...x, bookings: [...x.bookings, { ref, activityId, slotId, status: "Confirmed", checkIn: "Registered" }] }
      : x));
    return { ok: true, ref, activity, slot };
  }, [getTicket, getActivity, getSlot, slotRemaining]);

  const cancelBooking = useCallback((ticketCode, ref) => {
    setTickets((prev) => prev.map((t) => t.ticketCode === ticketCode
      ? { ...t, bookings: t.bookings.map((b) => (b.ref === ref ? { ...b, status: "Cancelled" } : b)) }
      : t));
  }, []);

  const setCheckIn = useCallback((ticketCode, ref, status) => {
    setTickets((prev) => prev.map((t) => t.ticketCode === ticketCode
      ? { ...t, bookings: t.bookings.map((b) => (b.ref === ref ? { ...b, checkIn: status } : b)) }
      : t));
  }, []);

  const cancelTicket = useCallback((ticketCode) => {
    setTickets((prev) => prev.map((t) => (t.ticketCode === ticketCode ? { ...t, ticketStatus: "Cancelled" } : t)));
  }, []);

  // admin auth
  const login = useCallback((email, password) => {
    if (email.trim().toLowerCase() === ADMIN_USER.email && password === ADMIN_USER.password) {
      setAdmin(true);
      localStorage.setItem(LS_ADMIN, "1");
      return { ok: true };
    }
    return { ok: false, error: "Email atau password salah." };
  }, []);
  const logout = useCallback(() => {
    setAdmin(false);
    localStorage.removeItem(LS_ADMIN);
  }, []);

  const value = useMemo(() => ({
    tickets, activities, package: TICKET_PACKAGE,
    getActivity, getSlot, slotBooked, slotRemaining, activityStatus,
    getTicket, validateTicket, purchaseTicket, bookActivity, cancelBooking, setCheckIn, cancelTicket,
    admin, login, logout,
  }), [tickets, activities, getActivity, getSlot, slotBooked, slotRemaining, activityStatus, getTicket, validateTicket, purchaseTicket, bookActivity, cancelBooking, setCheckIn, cancelTicket, admin, login, logout]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
